import React, { useCallback, useEffect, useState } from "react";
import { cn } from "utils";
import Input from "components/Input";
import Select from "components/Select";
import SubmitButton from "components/SubmitButton";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { MCNDeductionInputs, mcnDeductionSchema } from "./const";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createMiscDeduction, getMiscDeduction, updateMiscDeduction } from "services/deductions";
import { getEmployees } from "services/employees";
import { useNavigate, useSearchParams } from "react-router-dom";

const unwrap = (res) => Array.isArray(res) ? res : (res?.data ?? res?.items ?? []);
const toISO = (dateStr) => dateStr ? new Date(dateStr).toISOString() : null;
const toDateInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d)) return '';
    return d.toISOString().split('T')[0];
};

const AddEditMcnDeduction = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [searchParams] = useSearchParams();
    const editId = searchParams.get('id');
    const isEditMode = !!editId;

    const { data: employeesRaw = [] } = useQuery({
        queryKey: ['employees'],
        queryFn: async () => unwrap(await getEmployees()),
    });

    const { data: deductionData } = useQuery({
        queryKey: ['misc-deduction', editId],
        queryFn: () => getMiscDeduction(editId),
        enabled: isEditMode,
    });

    const employeeList = Array.isArray(employeesRaw) ? employeesRaw : (employeesRaw?.data ?? []);
    const employeeOptions = employeeList.map(e => ({
        value: e.id,
        label: [e.firstName, e.lastName].filter(Boolean).join(' ') || e.id,
    }));

    const { mutate: mutateCreate, isPending: isPendingCreate, isError: isErrorCreate, error: errorCreate } = useMutation({
        mutationFn: createMiscDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['misc-deductions'] });
            navigate('/deductions');
        },
    });

    const { mutate: mutateUpdate, isPending: isPendingUpdate, isError: isErrorUpdate, error: errorUpdate } = useMutation({
        mutationFn: updateMiscDeduction,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['misc-deductions'] });
            navigate('/deductions');
        },
    });

    const isPending = isEditMode ? isPendingUpdate : isPendingCreate;
    const isError = isEditMode ? isErrorUpdate : isErrorCreate;
    const error = isEditMode ? errorUpdate : errorCreate;

    const [deductMode, setDeductMode] = useState('amount');

    const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm({
        resolver: yupResolver(mcnDeductionSchema)
    });

    useEffect(() => {
        if (isEditMode && deductionData) {
            const d = deductionData;
            if (d.percent != null && !d.amount) setDeductMode('percent');
            reset({
                [MCNDeductionInputs.ASSIGNED_EMPLOYEE]: d.employeeId || '',
                [MCNDeductionInputs.DESCRIPTION]: d.description || '',
                [MCNDeductionInputs.EFFECTIVE_START]: toDateInput(d.effectiveStart || d.effective),
                [MCNDeductionInputs.EFFECTIVE_END]: toDateInput(d.effectiveEnd),
                [MCNDeductionInputs.AMOUNT]: d.amount != null ? String(d.amount) : '',
                [MCNDeductionInputs.PERCENT]: d.percent != null ? String(d.percent) : '',
                [MCNDeductionInputs.ANNUAL_LIMIT_AMOUNT]: d.annualLimitAmount != null ? String(d.annualLimitAmount) : '',
                [MCNDeductionInputs.TOTAL_LIMIT_AMOUNT]: d.totalLimitAmount != null ? String(d.totalLimitAmount) : '',
            });
        }
    }, [isEditMode, deductionData, reset]);

    const handleDeductModeToggle = (mode) => {
        setDeductMode(mode);
        setValue(MCNDeductionInputs.AMOUNT, '');
        setValue(MCNDeductionInputs.PERCENT, '');
    };

    const onSubmit = useCallback((values) => {
        const payload = {
            employeeId: values[MCNDeductionInputs.ASSIGNED_EMPLOYEE],
            description: values[MCNDeductionInputs.DESCRIPTION],
            amount: parseFloat(values[MCNDeductionInputs.AMOUNT]) || null,
            percent: parseFloat(values[MCNDeductionInputs.PERCENT]) || null,
            annualLimitAmount: parseFloat(values[MCNDeductionInputs.ANNUAL_LIMIT_AMOUNT]) || null,
            totalLimitAmount: parseFloat(values[MCNDeductionInputs.TOTAL_LIMIT_AMOUNT]) || null,
        };
        const effStart = toISO(values[MCNDeductionInputs.EFFECTIVE_START]);
        const effEnd = toISO(values[MCNDeductionInputs.EFFECTIVE_END]);
        if (effStart) payload.effectiveStart = effStart;
        if (effEnd) payload.effectiveEnd = effEnd;
        if (isEditMode) {
            mutateUpdate({ deductionId: editId, ...payload });
        } else {
            mutateCreate(payload);
        }
    }, [isEditMode, editId, mutateCreate, mutateUpdate]);

    return (
        <form className={cn("container-spacing pt-3 pb-5")} onSubmit={handleSubmit(onSubmit)}>
            <h3 className={cn("title mt-2")}>{isEditMode ? 'Edit Miscellaneous Deduction' : 'Add Miscellaneous Deduction'}</h3>
            <div>
                <div>
                    <div className={cn("container-fluid padding-0")}>
                        <div className={cn("row")}>
                            <div className="col-lg-8 col-12">
                                <Select
                                    label="Assigned Employee*"
                                    fullWidth
                                    placeholder="Select employee"
                                    options={employeeOptions}
                                    {...register(MCNDeductionInputs.ASSIGNED_EMPLOYEE)}
                                    error={!!errors[MCNDeductionInputs.ASSIGNED_EMPLOYEE]}
                                    errorText={errors[MCNDeductionInputs.ASSIGNED_EMPLOYEE]?.message}
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div>
                    <p className={cn("info-title mt-3")}>General Information</p>
                    <div className={cn("container-fluid padding-0")}>
                        <div className={cn("row")}>
                            <div className="col-lg-8 col-12">
                                <Input
                                    label="Description*"
                                    fullWidth
                                    variant="flat"
                                    {...register(MCNDeductionInputs.DESCRIPTION)}
                                    error={!!errors[MCNDeductionInputs.DESCRIPTION]}
                                    errorText={errors[MCNDeductionInputs.DESCRIPTION]?.message}
                                />
                            </div>
                        </div>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <Input label="Start Date" fullWidth variant="flat" type="date"
                                    {...register(MCNDeductionInputs.EFFECTIVE_START)}
                                    error={!!errors[MCNDeductionInputs.EFFECTIVE_START]}
                                    errorText={errors[MCNDeductionInputs.EFFECTIVE_START]?.message}
                                />
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="End Date" fullWidth variant="flat" type="date"
                                    {...register(MCNDeductionInputs.EFFECTIVE_END)}
                                    error={!!errors[MCNDeductionInputs.EFFECTIVE_END]}
                                    errorText={errors[MCNDeductionInputs.EFFECTIVE_END]?.message}
                                />
                            </div>
                        </div>
                        <div className={cn("row g-3 mt-2")}>
                            <div className="col-lg-4 col-12">
                                <div style={{ fontSize: 13, fontWeight: 600, color: '#444', marginBottom: 6 }}>Deduction</div>
                                <div style={{ display: 'inline-flex', borderRadius: 6, overflow: 'hidden', border: '1px solid #dee2e6', marginBottom: 6 }}>
                                    <button type="button"
                                        style={{ padding: '4px 14px', background: deductMode === 'amount' ? '#1B75F0' : '#fff', color: deductMode === 'amount' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', cursor: 'pointer' }}
                                        onClick={() => handleDeductModeToggle('amount')}>$ Amount</button>
                                    <button type="button"
                                        style={{ padding: '4px 14px', background: deductMode === 'percent' ? '#1B75F0' : '#fff', color: deductMode === 'percent' ? '#fff' : '#666', fontWeight: 600, fontSize: 12, border: 'none', borderLeft: '1px solid #dee2e6', cursor: 'pointer' }}
                                        onClick={() => handleDeductModeToggle('percent')}>% Percent</button>
                                </div>
                                {deductMode === 'amount' ? (
                                    <Input label="Amount ($)" fullWidth variant="flat" type="number" placeholder="0.00"
                                        {...register(MCNDeductionInputs.AMOUNT)}
                                        error={!!errors[MCNDeductionInputs.AMOUNT]}
                                        errorText={errors[MCNDeductionInputs.AMOUNT]?.message}
                                    />
                                ) : (
                                    <Input label="Percent (%)" fullWidth variant="flat" type="number" placeholder="0.00"
                                        {...register(MCNDeductionInputs.PERCENT)}
                                        error={!!errors[MCNDeductionInputs.PERCENT]}
                                        errorText={errors[MCNDeductionInputs.PERCENT]?.message}
                                    />
                                )}
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="Annual Limit ($)" fullWidth variant="flat" type="number" placeholder="0.00"
                                    {...register(MCNDeductionInputs.ANNUAL_LIMIT_AMOUNT)}
                                    error={!!errors[MCNDeductionInputs.ANNUAL_LIMIT_AMOUNT]}
                                    errorText={errors[MCNDeductionInputs.ANNUAL_LIMIT_AMOUNT]?.message}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Resets every calendar year. Deduction stops once this limit is reached.</div>
                            </div>
                            <div className="col-lg-4 col-12">
                                <Input label="Lifetime Limit ($)" fullWidth variant="flat" type="number" placeholder="0.00"
                                    {...register(MCNDeductionInputs.TOTAL_LIMIT_AMOUNT)}
                                    error={!!errors[MCNDeductionInputs.TOTAL_LIMIT_AMOUNT]}
                                    errorText={errors[MCNDeductionInputs.TOTAL_LIMIT_AMOUNT]?.message}
                                />
                                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Total cap across all time. Deduction stops permanently once reached.</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-5">
                    {isError && (
                        <p className="text-danger mb-2">
                            {error?.response?.data?.message || error?.response?.data?.title || 'Failed to save deduction. Please try again.'}
                        </p>
                    )}
                    <SubmitButton submitText={isPending ? 'Saving...' : (isEditMode ? 'Update Deduction' : 'Add Deduction')} disabled={isPending} />
                </div>
            </div>
        </form>
    );
}

export default AddEditMcnDeduction;

